import { useEffect, useRef } from "react";
import {
  Flex,
  Select,
  Input,
  Radio,
  Text,
  Checkbox,
  TooltipWrapper
} from "@theorgchartdev/storybook";
import type { ListItemOption } from "@theorgchartdev/storybook/dist/components/List/ListItem";
import { ConfirmDeleteButton } from "@/appComponents/Navigation/RightSidebar/Utils/ConfirmDeleteButton";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";
import { BuildSystemFieldOptions } from "@/Utils/DataLib";
import { RulePairInput } from "@/appComponents/Blocks/Composites/RuleInput/RulePairInput/RulePairInput";
import { containerTypesEnum } from "./Grouping.utils";
import { appstr } from "@coreapp/utils/appstr";
import { QRulePair } from "@coreapp/CoreApp/QRulePair";
import {
  RSBFooter,
  RSBPrimaryButton,
  RSBSecondaryButton
} from "@/appComponents/Navigation/RightSidebar/Utils/RSBFooter";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { UIRuleError } from "@/appComponents/Blocks/Composites/RuleInput/ruleErrors";

type GroupingAction = {
  fApplyToManagers: boolean;
  fChildrenBoxStyle: number | null;
  fChildrenBoxStyleEnabled: boolean;
  fGroupingBoxStyle: number;
  fGroupingField: string | null;
  fGroupingFieldEnabled: boolean;
  fGroupingThreshold: number;
  conditionalEnabled?: boolean;
  fManagerGroupingStyle: "comgr" | "custom";
  fManagerGroupingBoxStyle: number | null;
  fManagerChildrenBoxStyle: number | null;
  fManagerChildrenBoxStyleEnabled: boolean;
};

type GroupPair = {
  fieldName: string;
  fieldID: string;
  operator: string;
  value: string;
};

type Group = {
  id?: number;
  name: string | null;
  logicalop?: string;
  pairs: GroupPair[];
  type?: string;
  conditionalEnabled?: boolean;
  actions: {
    grouping: GroupingAction;
  };
};

type StyleOption = {
  label: string | number;
  value: number | null;
  clickHandler?: () => void;
};

type EditGroupingProps = {
  group: Group | null | undefined;
  HandleUpdate: (key: string, value?: unknown) => void;
  onBackBtnClick: () => void;
  nameErrorShown?: boolean;
  nameErrorMessage?: string | null;
  fieldErrorShown?: boolean;
  fieldErrorMessage?: string | null;
  rulePairError?: UIRuleError | null;
  customGroupStyles?: StyleOption[];
  employeeBoxStyles?: StyleOption[];
  managerCustomGroupStyles?: StyleOption[];
  managerEmployeeBoxStyles?: StyleOption[];
  getIsNewGroupMode: () => boolean;
  onDelete: () => void;
};

export const EditGrouping = ({
  group,
  HandleUpdate,
  onBackBtnClick,
  nameErrorShown,
  nameErrorMessage,
  fieldErrorShown,
  fieldErrorMessage,
  rulePairError,
  customGroupStyles,
  employeeBoxStyles,
  managerCustomGroupStyles,
  managerEmployeeBoxStyles,
  getIsNewGroupMode,
  onDelete
}: EditGroupingProps) => {
  const { setCustomHeaderProps } = useRightSidebarContext();
  const onBackBtnClickRef = useRef(onBackBtnClick);
  onBackBtnClickRef.current = onBackBtnClick;
  useEffect(() => {
    setCustomHeaderProps(() => ({
      iconProps: {
        size: "xsmall",
        dataTestId: "edit-grouping-back-btn",
        onClick: () => {
          setCustomHeaderProps({});
          onBackBtnClickRef.current();
        }
      },
      icon: "chevron-left",
      title: appstr("Back to groups")
    }));

    return () => {
      setCustomHeaderProps({});
    };
  }, []);

  if (!group) return null;

  const fieldOptions = BuildSystemFieldOptions((target: any) => {
    const { fieldDef } = target.eventInfo;
    const fieldName = fieldDef.GetFieldName();
    HandleUpdate("fGroupingField", fieldName);
  });

  const hasCustomGroupStyles = customGroupStyles?.some(s => s.value != null);
  const groupingObj = group?.actions?.grouping;
  const resolvedChildrenBoxStyle = employeeBoxStyles?.some(
    s => s.value === groupingObj?.fChildrenBoxStyle
  )
    ? groupingObj?.fChildrenBoxStyle
    : (employeeBoxStyles?.[1]?.value ?? null);
  const containerType = groupingObj?.fGroupingBoxStyle;
  const conditionalEnabled = groupingObj?.conditionalEnabled || false;
  const rulePairs = group?.pairs || [];
  let conditionalRule = new QRulePair();
  if (rulePairs.length) {
    const rule = rulePairs[0];
    conditionalRule.InitRulePair(rule.fieldName, rule.fieldID, null, rule.operator, rule.value);
  } else {
    conditionalRule.InitRulePair("", "", null, "equals", "");
  }

  return (
    <Flex direction={"column"} width={"100%"} height={"100%"}>
      <Flex className={"ocn-sidebar-section-container"} flex="1" overflow="auto">
        <Flex
          className={"ocn-sidebar-section-content--divider"}
          direction={"column"}
          width={"100%"}
          padding={"12px 8px"}
          gap={"12px"}
        >
          <Input
            size={"medium"}
            type={"text"}
            label={appstr("Group name")}
            onChange={(e: any) => HandleUpdate("name", e.target?.value ?? "")}
            value={group?.name ?? ""}
            id={"edit-grouping-name-input"}
            dataTestId={chartModeTestId("sidebar", "grouping-name-input")}
            require
            status={nameErrorShown && nameErrorMessage ? "error" : undefined}
            message={nameErrorShown && nameErrorMessage ? nameErrorMessage : undefined}
          />
          <Select
            inputOnly={false}
            optionSize={"medium"}
            selectSize={"medium"}
            label={appstr("Group by field")}
            options={fieldOptions as any}
            value={groupingObj?.fGroupingField ? `Box-${groupingObj.fGroupingField}` : ""}
            dataTestId={"edit-grouping-field-select"}
            require
            status={fieldErrorShown && fieldErrorMessage ? "error" : undefined}
            message={fieldErrorShown && fieldErrorMessage ? fieldErrorMessage : undefined}
            listProps={{
              enableSearch: true
            }}
          />
          <Flex direction={"column"} gap={"8px"}>
            <Text size={"small"} modifier={"bold"}>
              {appstr("Group box style")}
            </Text>
            <Flex direction="column" gap={"4px"}>
              <Radio
                id={"edit-grouping-framed-toggle"}
                data-testid={chartModeTestId("sidebar", "grouping-framed-radio")}
                label={appstr("Framed")}
                checked={containerType === containerTypesEnum.groupFramed}
                onChange={() => HandleUpdate("toggleGroupStyle", containerTypesEnum.groupFramed)}
                labelInfo={appstr(
                  "A frame is placed around grouped records. Conditional formatting targeting framed grouped records still appear."
                )}
              />
              <Radio
                id={"edit-grouping-condensed-toggle"}
                data-testid={chartModeTestId("sidebar", "grouping-condensed-radio")}
                label={appstr("Condensed")}
                checked={containerType === containerTypesEnum.groupHeader}
                onChange={() => HandleUpdate("toggleGroupStyle", containerTypesEnum.groupHeader)}
                labelInfo={appstr(
                  "Grouped employee records appear in a list. The ‘Group by field’ value for those grouped records is displayed at the top of the group."
                )}
              />
              {hasCustomGroupStyles ? (
                <Radio
                  id={"edit-grouping-custom-toggle"}
                  data-testid={chartModeTestId("sidebar", "grouping-custom-radio")}
                  label={appstr("Custom")}
                  checked={
                    containerType !== containerTypesEnum.groupFramed &&
                    containerType !== containerTypesEnum.groupHeader &&
                    containerType !== containerTypesEnum.nameOnly
                  }
                  onChange={() => HandleUpdate("setCustomGroupStyle", undefined)}
                />
              ) : (
                <Radio
                  id={"edit-grouping-custom-toggle"}
                  data-testid={chartModeTestId("sidebar", "grouping-custom-radio")}
                  label={appstr("Custom")}
                  checked={false}
                  disabled={true}
                  tooltipProps={{
                    message: appstr(
                      "You do not have any custom group styles created. Create a custom group style in the 'Box styles' panel and apply it to a conditional grouping."
                    )
                  }}
                />
              )}
            </Flex>
            {containerType !== containerTypesEnum.groupFramed &&
              containerType !== containerTypesEnum.groupHeader &&
              containerType !== containerTypesEnum.nameOnly && (
                <Flex direction={"column"} gap={"8px"} padding={"0 0 0 30px"}>
                  <Select
                    inputOnly={true}
                    optionSize={"small"}
                    selectSize={"small"}
                    aria-label={appstr("Group box style")}
                    options={
                      (customGroupStyles ?? []).map(style => ({
                        ...style,
                        clickHandler: () => HandleUpdate("setCustomGroupStyle", style.value)
                      })) as any
                    }
                    value={containerType}
                    dataTestId={"edit-grouping-custom-style-select"}
                  />
                  <Select
                    inputOnly={false}
                    optionSize={"small"}
                    selectSize={"small"}
                    label={appstr("Employee box style")}
                    aria-label={appstr("Employee box style")}
                    options={
                      (employeeBoxStyles ?? []).map(style => ({
                        ...style,
                        clickHandler: () => HandleUpdate("fChildrenBoxStyle", style.value)
                      })) as any
                    }
                    value={resolvedChildrenBoxStyle ?? undefined}
                    dataTestId={"edit-grouping-employee-style-select"}
                  />
                </Flex>
              )}
          </Flex>
          <Flex
            className={"ocn-sidebar-section-content--divider"}
            direction={"column"}
            width={"100%"}
            padding={"12px 0px 12px"}
            gap={"12px"}
          >
            <Flex direction={"column"} gap={"8px"}>
              <Text size={"small"} modifier={"bold"}>
                {appstr("advancedSettings")}
              </Text>
              <Checkbox
                id={"edit-grouping-conditional-toggle"}
                data-testid={chartModeTestId("sidebar", "grouping-conditional-checkbox")}
                size={"medium"}
                label={"Conditional grouping"}
                checked={conditionalEnabled}
                onChange={() => HandleUpdate("conditionalEnabled", !conditionalEnabled)}
              />
              {conditionalEnabled && conditionalRule && (
                <>
                  <RulePairInput
                    dataTestIdPrefix={"edit-grouping-"}
                    editing={true}
                    onChange={(rule: QRulePair) => HandleUpdate("conditionalRule", rule)}
                    value={conditionalRule}
                    direction={"column"}
                    requiredMarker={true}
                    selectSize={"medium"}
                    fieldError={rulePairError?.fieldError?.[0]}
                    valueError={rulePairError?.valueError?.[0]}
                  />
                  {rulePairError?.generalRuleError?.[0]?.hasError && (
                    <span
                      className="ocn-form-input-message ocn-form-input-message--error"
                      role="alert"
                    >
                      {rulePairError.generalRuleError[0].errorString}
                    </span>
                  )}
                </>
              )}
            </Flex>
            <Flex direction={"column"} gap={"8px"}>
              <Text size={"small"} modifier={"bold"}>
                {appstr("Allow groups with managers")}
              </Text>
              <Flex direction={"row"}>
                <Radio
                  id={"edit-grouping-managers-yes"}
                  label={appstr("Yes")}
                  checked={groupingObj?.fApplyToManagers === true}
                  onChange={() => HandleUpdate("fApplyToManagers", true)}
                  data-testid={"edit-grouping-managers-yes"}
                />
                <Radio
                  id={"edit-grouping-managers-no"}
                  label={appstr("No")}
                  checked={groupingObj?.fApplyToManagers !== true}
                  onChange={() => HandleUpdate("fApplyToManagers", false)}
                  data-testid={"edit-grouping-managers-no"}
                />
              </Flex>
            </Flex>
            {groupingObj?.fApplyToManagers === true && (
              <Flex direction={"column"} gap={"8px"}>
                <Text size={"small"} modifier={"bold"}>
                  {appstr("Manager group box style")}
                </Text>
                <Flex direction={"column"} gap={"4px"}>
                  <Radio
                    id={"edit-grouping-mgr-style-comgr"}
                    label={appstr("Co-manager")}
                    checked={groupingObj.fManagerGroupingStyle !== "custom"}
                    onChange={() => HandleUpdate("toggleManagerGroupStyle", "comgr")}
                    data-testid={"edit-grouping-mgr-style-comgr"}
                  />
                  {hasCustomGroupStyles ? (
                    <Radio
                      id={"edit-grouping-mgr-style-custom"}
                      label={appstr("Custom")}
                      checked={groupingObj.fManagerGroupingStyle === "custom"}
                      onChange={() => HandleUpdate("setManagerCustomGroupStyle", undefined)}
                      data-testid={"edit-grouping-mgr-style-custom"}
                    />
                  ) : (
                    <TooltipWrapper
                      tooltipProps={{
                        message: appstr(
                          "You do not have any custom group styles created. Create a custom group style in the 'Box styles' panel and apply it to a conditional grouping."
                        )
                      }}
                    >
                      <span>
                        <Radio
                          id={"edit-grouping-mgr-style-custom"}
                          label={appstr("Custom")}
                          checked={false}
                          disabled={true}
                          data-testid={"edit-grouping-mgr-style-custom"}
                        />
                      </span>
                    </TooltipWrapper>
                  )}
                </Flex>
                {groupingObj.fManagerGroupingStyle === "custom" && (
                  <Flex direction={"column"} gap={"8px"} padding={"0 0 0 30px"}>
                    <Select
                      inputOnly={true}
                      optionSize={"small"}
                      selectSize={"small"}
                      aria-label={appstr("Group box style")}
                      options={
                        (managerCustomGroupStyles ?? []).map(style => ({
                          ...style,
                          clickHandler: () =>
                            HandleUpdate("setManagerCustomGroupStyle", style.value)
                        })) as unknown as ListItemOption[]
                      }
                      // null is the "Default to last used or only created" sentinel;
                      // coercing to undefined would break the round-trip.
                      value={groupingObj.fManagerGroupingBoxStyle as ListItemOption["value"]}
                      dataTestId={"edit-grouping-mgr-style-custom-group-select"}
                    />
                    <Select
                      inputOnly={false}
                      optionSize={"small"}
                      selectSize={"small"}
                      label={appstr("Employee box style")}
                      aria-label={appstr("Employee box style")}
                      options={
                        (managerEmployeeBoxStyles ?? []).map(style => ({
                          ...style,
                          clickHandler: () => HandleUpdate("fManagerChildrenBoxStyle", style.value)
                        })) as unknown as ListItemOption[]
                      }
                      value={groupingObj.fManagerChildrenBoxStyle ?? undefined}
                      dataTestId={"edit-grouping-mgr-style-employee-select"}
                    />
                  </Flex>
                )}
              </Flex>
            )}
            <Flex direction={"column"} gap={"8px"}>
              <Text size={"small"} modifier={"bold"}>
                {appstr("Minimum group size")}
              </Text>
              <Select
                inputOnly={true}
                optionSize={"small"}
                selectSize={"small"}
                options={
                  [1, 2, 3, 4, 5].map(i => ({
                    label: i,
                    value: i,
                    clickHandler: () => HandleUpdate("fGroupingThreshold", i)
                  })) as any
                }
                value={groupingObj?.fGroupingThreshold}
                dataTestId={"edit-grouping-threshold-select"}
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <RSBFooter
        leftSlot={
          !getIsNewGroupMode() ? (
            <ConfirmDeleteButton
              onSubmit={onDelete}
              tooltipMessage={appstr("Delete")}
              headerTitle={appstr("Delete group?")}
              triggerTestId="edit-grouping-delete-btn"
            >
              {appstr("Delete group confirm")}
            </ConfirmDeleteButton>
          ) : null
        }
      >
        <RSBSecondaryButton
          dataTestId={chartModeTestId("sidebar", "grouping-cancel-btn")}
          onClick={() => onBackBtnClick()}
        />
        <RSBPrimaryButton data-testid="edit-grouping-save-btn" onClick={() => HandleUpdate("save")}>
          {getIsNewGroupMode() ? appstr("Create group") : appstr("Save changes")}
        </RSBPrimaryButton>
      </RSBFooter>
    </Flex>
  );
};
